import React, { memo, useState, ReactNode, useMemo } from "react";
import { Typography, Grid, CardActionArea, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import AddIcon from "@material-ui/icons/Add";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import { navigate } from "@reach/router";
import { BackofficeApplicationResource } from "../../../../resources";
import { useInvalidateOnUnmount } from "../../../../utils";
import { BackofficeApplicationList } from "./BackofficeApplicationList";
import { FormattedMessage } from "react-intl";
import DescriptionIcon from "@material-ui/icons/Description";
import HistoryIcon from "@material-ui/icons/History";
import ViewDayIcon from "@material-ui/icons/ViewDay";
import { useCommonWidths } from "../../../../hooks";
import {
	headerHeightMobile,
	headerHeight,
	leftMenuWidth,
	leftMenuWidthMobile,
} from "../../../../constants";
import { useResource } from "rest-hooks";

interface LeftMenuProps {
	toggleMenuOpen: () => void;
}

export const LeftMenu = memo(({ toggleMenuOpen }: LeftMenuProps) => {
	const classes = useStyles();

	const allBackofficeApplications = useResource(BackofficeApplicationResource.listShape(), {});
	useInvalidateOnUnmount(BackofficeApplicationResource.listShape(), {});

	const runningBackofficeApplications = allBackofficeApplications.filter(
		(l) => l.fulfilled === false
	);
	const completedBackofficeApplications = allBackofficeApplications.filter(
		(l) => l.fulfilled === true
	);

	const MenuItems = [
		{
			mobileIcon: <DescriptionIcon />,
			text: (
				<FormattedMessage
					id="minside.menu.my_applications"
					defaultMessage="Mine søknader"
					description="My applications menu item"
				/>
			),
			link: null,
			onClick: () => navigate("/applications"),
			expansionData: null,
		} as MenuItemProps,
		{
			mobileIcon: <ViewDayIcon />,
			text: (
				<FormattedMessage
					id="minside.menu.my_active_contracts"
					defaultMessage="Løpende kontrakter"
					description="My active contracts menu item"
				/>
			),
			link: null,
			onClick: () => {},
			expansionData: runningBackofficeApplications,
		} as MenuItemProps,
		{
			mobileIcon: <HistoryIcon />,
			text: (
				<FormattedMessage
					id="minside.menu.previously_paid_backofficeApplications"
					defaultMessage="Oppgjorte kontrakter"
					description="Previously paid backofficeApplications menu item"
				/>
			),
			link: null,
			onClick: () => {},
			expansionData: completedBackofficeApplications,
		} as MenuItemProps,
		{
			mobileIcon: null,
			text: (
				<FormattedMessage
					id="minside.menu.new_application"
					defaultMessage="Søk lån"
					description="New application menu item"
				/>
			),
			link: "https://financiering.no",
			onClick: () => {},
			expansionData: null,
		} as MenuItemProps,
	];

	return (
		<Grid item className={classes.menu}>
			{MenuItems.map((mi, idx) => {
				return (
					<div key={idx}>
						<MenuItem
							onClick={mi.onClick}
							expansionData={mi.expansionData}
							link={mi.link}
							mobileIcon={mi.mobileIcon}
							text={mi.text}
							closeMenuDrawer={toggleMenuOpen}
						/>
					</div>
				);
			})}
		</Grid>
	);
});

const useStyles = makeStyles((theme: Theme) => ({
	menu: {
		width: leftMenuWidth,
		background: "white",
		height: `calc(100vh - ${headerHeight}px)`,
		borderRight: "1px solid lightgrey",
		overflow: "auto",
		[theme.breakpoints.down("sm")]: {
			paddingBottom: "30vh",
			height: `calc(100vh - ${headerHeightMobile}px)`,
			width: leftMenuWidthMobile,
		},
	},
	menuItemContainer: {
		borderBottom: "1px solid lightgrey",
	},
	menuItem: {
		color: "#05a",
		padding: "20px 32px",
		[theme.breakpoints.down("sm")]: {
			padding: "8px 12px",
		},
	},
	link: {
		display: "flex",
		textDecoration: "underline",
		color: "#05a",
	},
	addIconButton: {
		[theme.breakpoints.up("md")]: {
			marginTop: 4,
		},
	},
	menuText: {
		flexGrow: 1,
		marginLeft: theme.spacing(1),
	},
	expansionArrow: {
		flexGrow: 1,
		textAlign: "right",
		alignSelf: "center"
	},
	errorTextNoBackofficeApplications: {
		padding: theme.spacing(1),
		textAlign: "center",
	},
}));

interface MenuItemProps {
	expansionData: BackofficeApplicationResource[] | null;
	link: string | null;
	mobileIcon: ReactNode;
	text: string | ReactNode;
	closeMenuDrawer?: () => void;
	onClick: () => void;
}

const MenuItem = (props: MenuItemProps) => {
	const classes = useStyles();
	const isMobile = useCommonWidths().isSmDown;

	const { expansionData, link, mobileIcon, text, closeMenuDrawer } = props;

	const [expanded, setExpanded] = useState(
		expansionData != null && expansionData.length > 0
	);

	return (
		<div className={classes.menuItemContainer}>
			<CardActionArea
				onClick={() => {
					props.onClick();
					expansionData
						? setExpanded(!expanded)
						: closeMenuDrawer && closeMenuDrawer();
				}}
			>
				<Grid container className={classes.menuItem} direction="row">
					{isMobile && mobileIcon}
					<Grid item>
						{link ? (
							<a
								href={link}
								target="_blank"
								rel="noopener noreferrer"
								className={classes.link}
							>
								<AddIcon className={classes.addIconButton} />
								<Typography
									className={classes.menuText}
									variant={isMobile ? "subtitle1" : "h6"}
								>
									{text}
								</Typography>
							</a>
						) : (
							<Typography
								className={classes.menuText}
								variant={isMobile ? "subtitle1" : "h6"}
							>
								{text}
							</Typography>
						)}
					</Grid>
					<Grid item className={classes.expansionArrow}>
						{expansionData && (
							<ArrowUpwardIcon
								style={{
									transform: `rotate(${!expanded ? 180 : 0}deg)`,
								}}
							/>
						)}
					</Grid>
				</Grid>
			</CardActionArea>
			{expanded &&
				(expansionData && expansionData.length > 0 ? (
					<BackofficeApplicationList
						data={expansionData}
						closeMenuDrawer={() => closeMenuDrawer && closeMenuDrawer()}
					/>
				) : (
					<Typography className={classes.errorTextNoBackofficeApplications}>
						<FormattedMessage
							id="minside.leftMenu.no_BackofficeApllications_errorMessage"
							defaultMessage="Ingen avtaler"
							description="Errormessage for missing backofficeapplications"
						/>
					</Typography>
				))}
		</div>
	);
};
